<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import googleClient from 'dashboard/api/channel/googleClient';
import microsoftChannelClient from 'dashboard/api/channel/microsoftChannelClient';
import OnboardingAPI from 'dashboard/api/onboarding';
import EmailCheckInput from './EmailCheckInput.vue';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import { useRegisterOnboardingNav } from '../useOnboardingNav';

const OAUTH_MESSAGE_TYPE = 'onboarding_oauth_done';
const OAUTH_BROADCAST = 'onboarding_oauth';
const POLL_INTERVAL_MS = 1500;

const props = defineProps({
  // getting_started: email + booking placeholder; ai_widget: website widget only
  variant: {
    type: String,
    default: 'getting_started',
    validator: value => ['getting_started', 'ai_widget'].includes(value),
  },
});

const store = useStore();
const { t } = useI18n();
const screen = ref(
  props.variant === 'ai_widget' ? 'configure' : 'choose'
); // choose | email | configure
const emailInputRef = ref(null);

if (props.variant === 'ai_widget') {
  store.dispatch('onboarding/setChannelType', 'web_widget');
} else if (!['email', 'microsoft'].includes(store.state.onboarding.channelType)) {
  store.dispatch('onboarding/setChannelType', 'email');
}
const emailIsValid = ref(false);
const isAuthorizing = ref(false);
const isWaitingForAuth = ref(false);
const lastAuthUrl = ref('');
const providerNeedsChoice = ref(false);

let oauthPopup = null;
let pollTimer = null;
let popupWatchTimer = null;
let oauthBroadcast = null;

const channelType = computed({
  get: () => store.state.onboarding.channelType,
  set: value => store.dispatch('onboarding/setChannelType', value),
});

const isEmailChannel = computed(() =>
  ['email', 'microsoft'].includes(channelType.value)
);

const emailProvider = computed({
  get: () => store.state.onboarding.emailProvider || 'forward',
  set: value => store.dispatch('onboarding/setEmailProvider', value),
});

const email = computed({
  get: () => store.state.onboarding.channelConfig.email?.email || '',
  set: value =>
    store.dispatch('onboarding/setChannelConfig', {
      channel: 'email',
      config: { email: value },
    }),
});

const inferredWebsiteUrl = computed(
  () =>
    store.state.onboarding.websiteOrigin ||
    websiteOriginFrom(store.state.onboarding.domain)
);

const websiteUrl = computed({
  get: () =>
    store.state.onboarding.channelConfig.web_widget?.website_url ||
    inferredWebsiteUrl.value,
  set: value =>
    store.dispatch('onboarding/setChannelConfig', {
      channel: 'web_widget',
      config: { website_url: value },
    }),
});

function websiteOriginFrom(url) {
  if (!url) return '';
  try {
    return new URL(url.includes('://') ? url : `https://${url}`).origin;
  } catch {
    return url;
  }
}

function prefillWebsiteUrl() {
  const current = (
    store.state.onboarding.channelConfig.web_widget?.website_url || ''
  ).trim();
  if (current || !inferredWebsiteUrl.value) return;
  store.dispatch('onboarding/setChannelConfig', {
    channel: 'web_widget',
    config: { website_url: inferredWebsiteUrl.value },
  });
}

watch(
  channelType,
  value => {
    if (value === 'web_widget') prefillWebsiteUrl();
  },
  { immediate: true }
);

function onEmailProviderChange(provider) {
  if (provider === 'gmail' || provider === 'outlook') {
    emailProvider.value = provider;
    providerNeedsChoice.value = false;
    store.commit('onboarding/SET_ERROR', null);
    return;
  }
  if (provider === 'forward') {
    providerNeedsChoice.value = true;
    emailProvider.value = 'forward';
    return;
  }
  providerNeedsChoice.value = false;
}

const uiFlags = computed(() => store.state.onboarding.uiFlags);
const error = computed(() => store.state.onboarding.error);

const isOauthProvider = computed(
  () =>
    isEmailChannel.value && ['gmail', 'outlook'].includes(emailProvider.value)
);

const stepTitle = computed(() => {
  if (isWaitingForAuth.value) return waitingTitle.value;
  if (screen.value === 'email') {
    return t('ONBOARDING_WIZARD.CHANNEL.EMAIL_STEP_TITLE');
  }
  if (screen.value === 'configure') {
    return isEmailChannel.value
      ? t('ONBOARDING_WIZARD.CHANNEL.CONFIGURE_EMAIL_TITLE')
      : t('ONBOARDING_WIZARD.CHANNEL.CONFIGURE_WEBSITE_TITLE');
  }
  return t('ONBOARDING_WIZARD.CHANNEL.TITLE');
});

const stepDescription = computed(() => {
  if (isWaitingForAuth.value) {
    return t('ONBOARDING_WIZARD.CHANNEL.WAITING_DESCRIPTION');
  }
  if (screen.value === 'email') {
    return t('ONBOARDING_WIZARD.CHANNEL.EMAIL_STEP_DESCRIPTION');
  }
  if (screen.value === 'configure') {
    return isEmailChannel.value
      ? t('ONBOARDING_WIZARD.CHANNEL.CONFIGURE_EMAIL_DESCRIPTION')
      : t('ONBOARDING_WIZARD.CHANNEL.CONFIGURE_WEBSITE_DESCRIPTION');
  }
  return t('ONBOARDING_WIZARD.CHANNEL.DESCRIPTION');
});

const primaryLabel = computed(() => {
  if (screen.value === 'choose') {
    return t('ONBOARDING_WIZARD.CONTINUE');
  }
  if (screen.value === 'configure' && !isEmailChannel.value) {
    return t('ONBOARDING_WIZARD.CONTINUE');
  }
  if (
    (screen.value === 'email' || screen.value === 'configure') &&
    emailProvider.value === 'gmail'
  ) {
    return t('ONBOARDING_WIZARD.CHANNEL.AUTHORIZE_GMAIL');
  }
  if (
    (screen.value === 'email' || screen.value === 'configure') &&
    emailProvider.value === 'outlook'
  ) {
    return t('ONBOARDING_WIZARD.CHANNEL.AUTHORIZE_OUTLOOK');
  }
  return t('ONBOARDING_WIZARD.CONTINUE');
});

const waitingTitle = computed(() => {
  if (emailProvider.value === 'outlook') {
    return t('ONBOARDING_WIZARD.CHANNEL.WAITING_OUTLOOK_TITLE');
  }
  return t('ONBOARDING_WIZARD.CHANNEL.WAITING_GMAIL_TITLE');
});

function selectEmailChannel() {
  channelType.value = 'email';
  if (!emailProvider.value) emailProvider.value = 'forward';
}

function goToConfigure() {
  if (channelType.value === 'web_widget') prefillWebsiteUrl();
  store.commit('onboarding/SET_ERROR', null);
  screen.value = 'configure';
}

function goToEmail() {
  store.commit('onboarding/SET_ERROR', null);
  providerNeedsChoice.value = false;
  screen.value = 'email';
}

function goToChoose() {
  stopWaiting({ cancelled: false });
  store.commit('onboarding/SET_ERROR', null);
  screen.value = 'choose';
}

function selectProvider(provider) {
  providerNeedsChoice.value = false;
  emailProvider.value = provider;
}

const selectCardClass = selected =>
  selected
    ? 'border-woot-500 bg-woot-50/60 dark:bg-woot-900/20 ring-1 ring-woot-500'
    : 'border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:border-woot-300';

function clearOauthWatchers() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (popupWatchTimer) {
    clearInterval(popupWatchTimer);
    popupWatchTimer = null;
  }
  window.removeEventListener('message', onOauthMessage);
  window.removeEventListener('storage', onOauthStorage);
  if (oauthBroadcast) {
    oauthBroadcast.close();
    oauthBroadcast = null;
  }
}

function stopWaiting({ cancelled = false } = {}) {
  clearOauthWatchers();
  if (oauthPopup && !oauthPopup.closed) {
    oauthPopup.close();
  }
  oauthPopup = null;
  isWaitingForAuth.value = false;
  isAuthorizing.value = false;
  if (cancelled) {
    store.commit(
      'onboarding/SET_ERROR',
      t('ONBOARDING_WIZARD.CHANNEL.WAITING_CANCELLED')
    );
  }
}

async function finishOauthSuccess() {
  clearOauthWatchers();
  if (oauthPopup && !oauthPopup.closed) {
    oauthPopup.close();
  }
  oauthPopup = null;
  isWaitingForAuth.value = false;
  isAuthorizing.value = false;
  store.commit('onboarding/SET_ERROR', null);
  await store.dispatch('onboarding/fetchStatus', { syncStep: false });
  await store.dispatch('onboarding/provisionInbox');
  await store.dispatch('onboarding/setStep', 4);
}

function isOauthDonePayload(data) {
  return data?.type === OAUTH_MESSAGE_TYPE;
}

function onOauthMessage(event) {
  if (event.origin !== window.location.origin) return;
  if (!isOauthDonePayload(event.data)) return;
  finishOauthSuccess();
}

function onOauthStorage(event) {
  if (!event.key?.startsWith('onboarding_oauth_done:')) return;
  finishOauthSuccess();
}

function startOauthWatchers() {
  window.addEventListener('message', onOauthMessage);
  window.addEventListener('storage', onOauthStorage);

  if (typeof BroadcastChannel !== 'undefined') {
    oauthBroadcast = new BroadcastChannel(OAUTH_BROADCAST);
    oauthBroadcast.onmessage = event => {
      if (!isOauthDonePayload(event.data)) return;
      finishOauthSuccess();
    };
  }

  pollTimer = setInterval(async () => {
    try {
      const { data } = await OnboardingAPI.getStatus();
      if (data?.inbox_id) {
        await finishOauthSuccess();
      }
    } catch {
      // Keep waiting; transient network errors are fine.
    }
  }, POLL_INTERVAL_MS);

  popupWatchTimer = setInterval(async () => {
    if (!oauthPopup || !oauthPopup.closed) return;
    clearInterval(popupWatchTimer);
    popupWatchTimer = null;
    try {
      const { data } = await OnboardingAPI.getStatus();
      if (data?.inbox_id) {
        await finishOauthSuccess();
        return;
      }
    } catch {
      // Fall through to cancelled.
    }
    stopWaiting({ cancelled: true });
  }, 500);
}

async function authorizeProvider() {
  const address = email.value.trim();
  if (!address) {
    store.commit(
      'onboarding/SET_ERROR',
      t('ONBOARDING_WIZARD.CHANNEL.EMAIL_REQUIRED')
    );
    return;
  }

  isAuthorizing.value = true;
  store.commit('onboarding/SET_ERROR', null);
  try {
    await OnboardingAPI.updateChannel({
      channel_type: 'email',
      channel_config: { email: address },
      email_provider: emailProvider.value,
    });

    let url;
    if (emailProvider.value === 'gmail') {
      const response = await googleClient.generateAuthorization({
        email: address,
        onboarding: true,
      });
      url = response.data?.url;
    } else {
      const response = await microsoftChannelClient.create({
        email: address,
        onboarding: true,
      });
      url = response.data?.url;
    }

    if (!url) {
      throw new Error('Missing authorize URL');
    }

    lastAuthUrl.value = url;
    openOauthPopup(url);
  } catch (err) {
    store.commit(
      'onboarding/SET_ERROR',
      err?.response?.data?.error ||
        t('ONBOARDING_WIZARD.CHANNEL.AUTHORIZE_ERROR')
    );
    isAuthorizing.value = false;
    isWaitingForAuth.value = false;
  }
}

function openOauthPopup(url) {
  clearOauthWatchers();
  const width = 600;
  const height = 720;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  oauthPopup = window.open(
    url,
    'onboarding_oauth',
    `popup=yes,width=${width},height=${height},left=${left},top=${top}`
  );

  if (!oauthPopup) {
    store.commit(
      'onboarding/SET_ERROR',
      t('ONBOARDING_WIZARD.CHANNEL.POPUP_BLOCKED')
    );
    isAuthorizing.value = false;
    return;
  }

  isWaitingForAuth.value = true;
  isAuthorizing.value = false;
  startOauthWatchers();
}

function reopenOauthPopup() {
  store.commit('onboarding/SET_ERROR', null);
  if (lastAuthUrl.value) {
    openOauthPopup(lastAuthUrl.value);
    return;
  }
  authorizeProvider();
}

const continueSetup = async () => {
  if (screen.value === 'choose') {
    if (isEmailChannel.value) {
      goToEmail();
      return;
    }
    goToConfigure();
    return;
  }

  if (screen.value === 'email') {
    const result = await emailInputRef.value?.recheckNow?.();
    if (!result?.valid && !emailIsValid.value) return;
    if (result?.provider) onEmailProviderChange(result.provider);
    if (isOauthProvider.value && !providerNeedsChoice.value) {
      authorizeProvider();
      return;
    }
    // Unknown MX — ask how to connect.
    store.commit('onboarding/SET_ERROR', null);
    screen.value = 'configure';
    return;
  }

  if (screen.value === 'configure') {
    if (isEmailChannel.value) {
      if (isOauthProvider.value) {
        authorizeProvider();
        return;
      }
      store.dispatch('onboarding/saveChannel');
      return;
    }
    store.dispatch('onboarding/saveChannel');
  }
};

useRegisterOnboardingNav(() => {
  if (isWaitingForAuth.value) {
    return {
      hidePrimary: true,
      backLabel: t('ONBOARDING_WIZARD.CHANNEL.CANCEL_WAITING'),
      onBack: () => stopWaiting({ cancelled: true }),
    };
  }

  if (screen.value === 'email') {
    return {
      primary: {
        label: primaryLabel.value,
        disabled: !emailIsValid.value,
        loading:
          uiFlags.value.isSaving ||
          uiFlags.value.isProvisioning ||
          isAuthorizing.value,
        onClick: continueSetup,
      },
      onBack: goToChoose,
    };
  }

  if (screen.value === 'configure') {
    return {
      primary: {
        label: primaryLabel.value,
        disabled: isEmailChannel.value && !emailProvider.value,
        loading:
          uiFlags.value.isSaving ||
          uiFlags.value.isProvisioning ||
          isAuthorizing.value,
        onClick: continueSetup,
      },
      ...(props.variant === 'ai_widget'
        ? {}
        : {
            onBack: isEmailChannel.value ? goToEmail : goToChoose,
          }),
    };
  }

  return {
    primary: {
      label: primaryLabel.value,
      disabled: !channelType.value,
      onClick: continueSetup,
    },
  };
});

onBeforeUnmount(() => {
  clearOauthWatchers();
});

const inputClass =
  'w-full h-12 px-4 text-base rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500 !mb-0';
</script>

<template>
  <OnboardingStepLayout
    :step-label="t('ONBOARDING_WIZARD.PHASES.3.LABEL')"
    :title="stepTitle"
    :description="stepDescription"
    wide
  >
    <div
      v-if="isWaitingForAuth"
      class="w-full flex flex-col items-start justify-center gap-4 rounded-xl border border-slate-100 dark:border-slate-900 bg-white dark:bg-slate-900/40 shadow-sm p-10 min-h-[16rem]"
    >
      <Spinner class="text-woot-500" />
      <p class="text-base text-slate-600 dark:text-slate-300 m-0">
        {{ t('ONBOARDING_WIZARD.CHANNEL.WAITING_HINT') }}
      </p>
      <p
        v-if="error"
        class="text-sm text-red-600 dark:text-red-400 m-0"
      >
        {{ error }}
      </p>
      <Button
        md
        slate
        outline
        icon="i-lucide-external-link"
        :label="t('ONBOARDING_WIZARD.CHANNEL.REOPEN_POPUP')"
        @click="reopenOauthPopup"
      />
    </div>

    <div
      v-else-if="screen === 'choose'"
      class="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full"
    >
      <button
        type="button"
        class="rounded-2xl border p-6 text-left transition-all min-h-[11rem] flex flex-col"
        :class="selectCardClass(isEmailChannel)"
        @click="selectEmailChannel"
      >
        <Icon icon="i-lucide-mail" class="size-7 text-woot-500 mb-4" />
        <p class="font-semibold text-slate-900 dark:text-white m-0 text-lg">
          {{ t('ONBOARDING_WIZARD.CHANNEL.EMAIL') }}
        </p>
        <p class="text-sm text-slate-500 dark:text-slate-400 m-0 mt-2">
          {{ t('ONBOARDING_WIZARD.CHANNEL.EMAIL_HELP') }}
        </p>
      </button>
      <div
        class="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-6 text-left min-h-[11rem] flex flex-col opacity-60 cursor-not-allowed"
        aria-disabled="true"
      >
        <div class="flex items-center justify-between gap-2 mb-4">
          <Icon icon="i-lucide-calendar-range" class="size-7 text-slate-400" />
          <span
            class="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          >
            {{ t('ONBOARDING_WIZARD.CHANNEL.BOOKING_WIDGET_SOON') }}
          </span>
        </div>
        <p class="font-semibold text-slate-900 dark:text-white m-0 text-lg">
          {{ t('ONBOARDING_WIZARD.CHANNEL.BOOKING_WIDGET') }}
        </p>
        <p class="text-sm text-slate-500 dark:text-slate-400 m-0 mt-2">
          {{ t('ONBOARDING_WIZARD.CHANNEL.BOOKING_WIDGET_HELP') }}
        </p>
      </div>
    </div>

    <div
      v-else-if="screen === 'configure' && isEmailChannel"
      class="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full"
    >
      <button
        type="button"
        class="rounded-2xl border p-6 text-left transition-all min-h-[10rem] flex flex-col"
        :class="selectCardClass(emailProvider === 'gmail')"
        @click="selectProvider('gmail')"
      >
        <Icon icon="i-woot-gmail" class="size-7 mb-4" />
        <p class="font-semibold text-slate-900 dark:text-white m-0 text-lg">
          {{ t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_GMAIL') }}
        </p>
        <p class="text-sm text-slate-500 dark:text-slate-400 m-0 mt-2">
          {{ t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_GMAIL_HELP') }}
        </p>
      </button>
      <button
        type="button"
        class="rounded-2xl border p-6 text-left transition-all min-h-[10rem] flex flex-col"
        :class="selectCardClass(emailProvider === 'outlook')"
        @click="selectProvider('outlook')"
      >
        <Icon icon="i-woot-outlook" class="size-7 mb-4" />
        <p class="font-semibold text-slate-900 dark:text-white m-0 text-lg">
          {{ t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_OUTLOOK') }}
        </p>
        <p class="text-sm text-slate-500 dark:text-slate-400 m-0 mt-2">
          {{ t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_OUTLOOK_HELP') }}
        </p>
      </button>
      <button
        type="button"
        class="rounded-2xl border p-6 text-left transition-all min-h-[10rem] flex flex-col"
        :class="selectCardClass(emailProvider === 'forward')"
        @click="selectProvider('forward')"
      >
        <Icon icon="i-lucide-forward" class="size-7 text-woot-500 mb-4" />
        <p class="font-semibold text-slate-900 dark:text-white m-0 text-lg">
          {{ t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_FORWARD') }}
        </p>
        <p class="text-sm text-slate-500 dark:text-slate-400 m-0 mt-2">
          {{ t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_FORWARD_HELP') }}
        </p>
      </button>
    </div>

    <div v-else-if="screen === 'email'" class="w-full flex flex-col gap-4">
      <EmailCheckInput
        ref="emailInputRef"
        v-model="email"
        :label="t('ONBOARDING_WIZARD.CHANNEL.EMAIL_ADDRESS')"
        :placeholder="t('ONBOARDING_WIZARD.CHANNEL.EMAIL_PLACEHOLDER')"
        @validity-change="emailIsValid = $event"
        @provider-change="onEmailProviderChange"
      />
      <p v-if="error" class="text-sm text-red-600 dark:text-red-400 m-0">
        {{ error }}
      </p>
    </div>

    <div v-else class="w-full flex flex-col gap-4">
      <label class="flex flex-col gap-1.5">
        <span class="text-sm font-semibold text-slate-700 dark:text-slate-200">
          {{ t('ONBOARDING_WIZARD.CHANNEL.WEBSITE_URL') }}
        </span>
        <input
          v-model="websiteUrl"
          type="url"
          :class="inputClass"
          :placeholder="
            inferredWebsiteUrl ||
            t('ONBOARDING_WIZARD.CHANNEL.WEBSITE_URL_PLACEHOLDER')
          "
        />
      </label>
      <p
        v-if="inferredWebsiteUrl && websiteUrl === inferredWebsiteUrl"
        class="text-sm text-slate-500 dark:text-slate-400 m-0"
      >
        {{ t('ONBOARDING_WIZARD.CHANNEL.WEBSITE_URL_HINT') }}
      </p>
      <p v-if="error" class="text-sm text-red-600 dark:text-red-400 m-0">
        {{ error }}
      </p>
    </div>
  </OnboardingStepLayout>
</template>
